import { isNumeric } from '../helpers';
import { Unit, UnitCategory, getUnitCategory, LENGTH, TEMPERATURE} from './units';
import { BaseConverter, UnitConversionError } from './converters/base-converter';
import { LENGTH_CONVERTER } from './converters/length-converter';
import { TEMPERATURE_CONVERTER } from './converters/temperature-converter';


/**
 * Converter map (maps unit category to the appropriate unit converter singleton).
 */
export const CONVERTERS: Map<UnitCategory, BaseConverter<Unit>> = new Map<UnitCategory, BaseConverter<Unit>>([
  [LENGTH, LENGTH_CONVERTER],
  [TEMPERATURE, TEMPERATURE_CONVERTER]
]);


/**
 * Unit converter class definition
 */
export class Converter<T extends Unit> {
  /**
   * Converts a value from one unit to another.
   * @param {number} value Value to convert
   * @param {T} from Source unit
   * @param {T} to Destination unit
   * @return {number} Converted value in the destination unit.
   */
  convert(value: number, from: T, to: T): number {
    this._ensureValidValue(value);
    const category = this._ensureUnitCategoriesMatch(from, to);
    const converter = CONVERTERS.get(category);
    if (!converter) {
      throw new Error(`Unable to find appropriate unit converter singleton for unit category "${category}"`);
    }
    return converter.convert(value, from, to);
  }

  /**
   * Ensures the given value is valid for conversion (finite number), throwing an error if not.
   * @param value {any}
   * @private
   */
  private _ensureValidValue(value: any): void {
    const prefix = `Invalid value for conversion: "${value}"`;
    if (value === null || value === undefined) {
      throw new UnitConversionError(`${prefix}. Value must not be null or undefined.`);
    }
    if (!isNumeric(value)) {
      throw new UnitConversionError(`${prefix}. Value must be numeric.`);
    }
    if (isNaN(value)) {
      throw new UnitConversionError(`${prefix}. Value may not be NaN.`);
    }
    if (!isFinite(value)) {
      throw new UnitConversionError(`${prefix}. Value must be finite.`);
    }
  }

  /**
   * Ensures that the source and destination unit categories are valid and match (throwing an error if they do not), and
   * returns the common unit category.
   * @param {Unit} from Source unit
   * @param {Unit} to Destination unit
   * @return {UnitCategory} Unit category of source and destination units (assuming the unit categories are valid and
   *  match).
   * @private
   */
  private _ensureUnitCategoriesMatch(from: Unit, to: Unit): UnitCategory {
    const fromType = getUnitCategory(from);
    if (!fromType) {
      throw new UnitConversionError(`Unable to determine category of source unit: "${from}".`);
    }
    const toType = getUnitCategory(to);
    if (!toType) {
      throw new UnitConversionError(`Unable to determine category of destination unit: "${to}".`);
    }
    if (fromType !== toType) {
      throw new UnitConversionError(`Unable to convert between incompatible units "${fromType}" and "${toType}").`);
    }
    return fromType;
  }
}


/**
 * Unit converter singleton
 */
export const UnitConverter: Converter<Unit> = new Converter<Unit>();
